import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Users, VolumeX, CheckCircle2, PauseCircle, AlertCircle, XCircle } from "lucide-react";
import { useHistory } from "react-router-dom";
import { fetchWithAuth } from "../../../api-client/APIClient";
import { LinkedAccount, LinkedAccountMetrics } from "../../../models/Entities";
import { navigateToLinkedAccountsTablePage, useQuery } from "../../../router/RouterUtils";
import IntegrationsManagementFilter from "../shared/IntegrationsManagementFilter";
import PaginationFooter from "../../shared/PaginationFooter";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import LinkedAccountDeletionModal from "./shared/LinkedAccountDeletionModal";
import MaybeDisable from "../../shared/MaybeDisable";
import { useMemo } from "react";
import { LinkedAccountStatuses } from "../../../constants";
import LinkedAccountTable from "./LinkedAccountTable";
import { Button, Alert, Text, ButtonVariant, Card } from "@merge-api/merge-javascript-shared";
import { useLazyRequest } from "../../shared/hooks/useLazyRequest";
import LinkedAccountsPageFilterCard from "./LinkedAccountsPageFilterCard";

type Props = {
  isTestAccounts: boolean;
  linkedAccounts: LinkedAccount[] | undefined;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onRemove: (index: number) => void;
  onRefetch: (cursor?: any) => void;
  isLoading: boolean;
  onLinkedAccountsParamsPathChange: (paramsPath: string) => void;
};

const LinkedAccountsPageData = (props: Props) => {
  const history = useHistory();
  const [linkedAccountMetrics, setLinkedAccountMetrics] = useState<LinkedAccountMetrics>();
  const [selectedStatus, setSelectedStatus] = useState(LinkedAccountStatuses.ALL);
  const [paramsPath, setParamsPath] = useState<string | undefined>(undefined);
  const [linkedAccountsBeingResynced, setLinkedAccountsBeingResynced] = useState<
    Set<LinkedAccount>
  >(new Set());
  const [linkedAccountToDelete, setLinkedAccountToDelete] = useState<LinkedAccount | undefined>();
  const isLinkedAccountDeletionModalOpen = linkedAccountToDelete != undefined;
  const query = useQuery();
  const [fetchDetailedCounts, _] = useLazyRequest<LinkedAccountMetrics>({});

  const getLinkedAccountParams = () => {
    return selectedStatus !== "ALL" ? `status=${selectedStatus}` : "";
  };

  const completeProductionDuplicatesOf = useMemo<string | null>(
    () => query.get("complete_production_duplicates_of"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const showDuplicatesOnly = !!completeProductionDuplicatesOf;

  const testAccountPath = props.isTestAccounts ? "&is_test_account=true" : "";

  const getLinkedAccountParamsPath = () => {
    let result = getLinkedAccountParams();

    if (paramsPath) {
      if (result) {
        result += `&${paramsPath}`;
      } else {
        result = paramsPath;
      }
    }

    if (testAccountPath) {
      if (result) {
        result += `&${testAccountPath}`;
      } else {
        result = testAccountPath;
      }
    }

    return result;
  };

  const updateDetailedCounts = async () => {
    const { data } = await fetchDetailedCounts({
      path: `/integrations/linked-accounts/detailed-counts?${getLinkedAccountParamsPath()}`,
    });
    setLinkedAccountMetrics(data);
  };

  useEffect(() => {
    if (paramsPath == undefined) {
      return;
    }
    updateDetailedCounts();
  }, [paramsPath]);

  useEffect(() => {
    const status = query.get("status");
    if (status) {
      setSelectedStatus(status);
    }
  }, [location.search]);

  useEffect(() => {
    const newParamsPath = getLinkedAccountParamsPath();
    history.replace({
      pathname: location.pathname,
      search: newParamsPath,
    });

    props.onLinkedAccountsParamsPathChange(getLinkedAccountParamsPath());
  }, [paramsPath, selectedStatus, location]);

  const forceResync = (linkedAccount: LinkedAccount) => {
    setLinkedAccountsBeingResynced(new Set(linkedAccountsBeingResynced).add(linkedAccount));
    fetchWithAuth({
      path: `/integrations/linked-accounts/${linkedAccount.id}/force-resync`,
      method: "POST",
      onResponse: (_) => {
        const newSet = new Set(linkedAccountsBeingResynced);
        newSet.delete(linkedAccount);
        setLinkedAccountsBeingResynced(newSet);
        showSuccessToast(
          `Resync queued for ${linkedAccount.end_user.organization_name} integration with ${linkedAccount.integration.name}.`,
        );
      },
      onError: () => {
        const newSet = new Set(linkedAccountsBeingResynced);
        newSet.delete(linkedAccount);
        setLinkedAccountsBeingResynced(newSet);
        showErrorToast("Failed to force resync. Please try again.");
      },
    });
  };

  const deleteLinkedAccount = () => {
    if (linkedAccountToDelete && props.linkedAccounts) {
      fetchWithAuth({
        path: `/integrations/linked-accounts/${linkedAccountToDelete.id}`,
        method: "DELETE",
        onResponse: () => {
          const newSet = new Set(linkedAccountsBeingResynced);
          newSet.delete(linkedAccountToDelete);
          setLinkedAccountsBeingResynced(newSet);
          showSuccessToast("Deleted linked account!");
          setLinkedAccountToDelete(undefined);
          const indexToRemove = props.linkedAccounts?.findIndex(
            (linkedAccount) => linkedAccount.id == linkedAccountToDelete.id,
          );
          if (indexToRemove || indexToRemove === 0) {
            props.onRemove(indexToRemove);
          }
        },
        onError: () => {
          showErrorToast("Failed to delete linked account. Please try again.");
        },
      });
    }
  };

  const onIgnoreDuplicatesClick = () => {
    fetchWithAuth({
      path: `/integrations/linked-accounts/${completeProductionDuplicatesOf}/ignore-duplicates`,
      method: "POST",
      onResponse: () => {
        showSuccessToast("Successfully ignored accounts with these credentials!");
        navigateToLinkedAccountsTablePage(history);
      },
      onError: () => {
        showErrorToast("Failed to ignore accounts with these credentials.");
      },
    });
  };

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(selectedStatus === status ? LinkedAccountStatuses.ALL : status);
  };

  const hasPagination = props.hasNext || props.hasPrevious;
  return (
    <>
      {linkedAccountToDelete && (
        <LinkedAccountDeletionModal
          isModalOpen={isLinkedAccountDeletionModalOpen}
          deleteLinkedAccount={deleteLinkedAccount}
          closeModal={() => setLinkedAccountToDelete(undefined)}
          linkedAccount={linkedAccountToDelete}
        />
      )}
      {showDuplicatesOnly && (
        <Alert className="mt-2 mb-6" icon={<Users size={16} />}>
          <div className="flex flex-row w-full items-center justify-between gap-x-2">
            <div className="flex flex-col">
              <Text variant="h6">
                These Linked Accounts are configured using the same credentials
              </Text>
              <Text>
                If this is intentional, press &quot;Ignore&quot; to dismiss warnings for these
                accounts. Otherwise, delete unwanted Linked Accounts to avoid unnecessary syncing.
              </Text>
            </div>
            <Button
              size="sm"
              leftIcon={<VolumeX size={12} />}
              onClick={onIgnoreDuplicatesClick}
              variant={ButtonVariant.TertiaryWhite}
            >
              Ignore
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <LinkedAccountsPageFilterCard
          selected={selectedStatus === LinkedAccountStatuses.COMPLETE}
          onClick={() => {
            handleStatusSelect(LinkedAccountStatuses.COMPLETE);
          }}
          title={
            <>
              <CheckCircle2 size={16} className="text-green-50" />
              <Text variant="h6">Linked</Text>
            </>
          }
          metric={
            linkedAccountMetrics
              ? linkedAccountMetrics.completed_linked_accounts_count +
                (linkedAccountMetrics.rippling_oauth_accounts_count ?? 0) -
                (showDuplicatesOnly ? linkedAccountMetrics.idle_accounts_count : 0)
              : undefined
          }
        />
        <LinkedAccountsPageFilterCard
          selected={selectedStatus === LinkedAccountStatuses.IDLE}
          onClick={() => {
            handleStatusSelect(LinkedAccountStatuses.IDLE);
          }}
          title={
            <>
              <PauseCircle size={16} className="text-gray-50" />
              <Text variant="h6">Idle</Text>
            </>
          }
          metric={linkedAccountMetrics?.idle_accounts_count}
        />

        <LinkedAccountsPageFilterCard
          selected={selectedStatus === LinkedAccountStatuses.INCOMPLETE}
          onClick={() => {
            handleStatusSelect(LinkedAccountStatuses.INCOMPLETE);
          }}
          title={
            <>
              <AlertCircle size={16} className="text-yellow-50" />
              <Text variant="h6">Incomplete</Text>
            </>
          }
          metric={linkedAccountMetrics?.incomplete_linked_accounts_count}
        />

        <LinkedAccountsPageFilterCard
          selected={selectedStatus === LinkedAccountStatuses.RELINK_NEEDED}
          onClick={() => {
            handleStatusSelect(LinkedAccountStatuses.RELINK_NEEDED);
          }}
          title={
            <>
              <XCircle size={16} className="text-red-50" />
              <Text variant="h6">Relink</Text>
            </>
          }
          metric={linkedAccountMetrics?.relink_needed_accounts_count}
        />
      </div>
      <MaybeDisable disable={showDuplicatesOnly}>
        <IntegrationsManagementFilter
          isLinkedAccounts
          setParamsPath={setParamsPath}
          isTestAccounts={props.isTestAccounts}
        />
      </MaybeDisable>
      <Row>
        <Col md={12}>
          <LinkedAccountTable
            linkedAccounts={props.linkedAccounts}
            isLoading={props.isLoading}
            forceResync={forceResync}
            isTestAccounts={props.isTestAccounts}
            setLinkedAccountToDelete={setLinkedAccountToDelete}
          />
        </Col>
      </Row>

      {hasPagination && (
        <PaginationFooter
          hasPrevious={props.hasPrevious}
          hasNext={props.hasNext}
          onPreviousClick={props.onPrevious}
          onNextClick={props.onNext}
        />
      )}
    </>
  );
};

export default LinkedAccountsPageData;
